export type PageResult<T> = { items: T[]; page: number; pageSize: number; total: number };

export type PortalTaskGroupKey = 'todo' | 'done' | 'cc' | 'initiated';
export type PortalWorkItemAction = {
  actionCode: string;
  label: string;
  emphasis: 'default' | 'primary' | 'warning' | 'danger';
  requiresComment: boolean;
  operationType: 'SHELL_LOCAL' | 'WORKFLOW';
};

export type PortalWorkItemSummaryItem = { label: string; value: string };

export type PortalWorkItemListItem = {
  taskId: string;
  /** Present only on the separate initiated-instance API and view model. */
  initiatedCardPublicId?: string;
  processInstancePublicId?: string;
  category: PortalTaskGroupKey;
  sourceSystem: string;
  title: string;
  processDefinitionName: string;
  taskName: string;
  status: string;
  sourceState: string;
  sourceVersion: string;
  initiator: string;
  initiatorAvatarUrl?: string;
  currentStage: string;
  receivedAt: string;
  updatedAt: string;
  summary: string;
  urgent: boolean;
  unread: boolean;
  relatedObject: { name: string; code: string; routePath: string };
  summaryItems: PortalWorkItemSummaryItem[];
  quickApprovable: boolean;
  quickApprovalLabel?: string | null;
  runtimeTaskVersion: number;
};

export type PortalTaskAttachment = {
  fileAssetPublicId: string;
  name: string;
  size: string;
  type: 'pdf' | 'word' | 'excel' | 'other';
  mimeType: string;
};

export type PortalUploadedAttachment = {
  fileAssetPublicId: string;
  fileName: string;
  fileHash: string;
  mimeType: string;
  sizeBytes: number;
  sortNo: number;
};

export type PortalAttachmentUploadIntent = {
  fileAssetPublicId: string;
  objectKey: string;
  uploadUrl: string;
  headers: Record<string, string>;
  expiresAt: string;
};

export type PortalTaskTraceNode = {
  nodeId: string;
  itemKind: 'PROCESS_START' | 'NODE' | 'PROCESS_END';
  nodeName: string;
  nodeRole?: string;
  status: 'done' | 'active' | 'pending';
  itemState: string;
  firstSequence: number;
  startedAt: string;
  endedAt?: string;
  records: Array<{
    operationId?: string;
    participantId?: string;
    actor: string;
    actorAvatarUrl?: string;
    actionCode?: string;
    action: string;
    opinion?: string;
    handledAt: string;
    attachments: PortalTaskAttachment[];
  }>;
};

export type PortalTaskComment = {
  commentId: string;
  parentCommentId?: string | null;
  author: string;
  authorAvatarUrl?: string;
  content: string;
  createdAt: string;
  replyTo?: string;
  parentContent?: string | null;
  parentDeleted?: boolean;
  deletable?: boolean;
};

export type PortalWorkItemDetail = {
  summary: PortalWorkItemListItem;
  processInstanceId: string;
  pageRoute: string;
  traceNodes: PortalTaskTraceNode[];
  availableActions: PortalWorkItemAction[];
  attachments: PortalTaskAttachment[];
  comments: PortalTaskComment[];
  flowPageContext: {
    contextType: 'WORK_ITEM';
    applicationCode: string;
    processId: string;
    processVersion: number;
    definitionVersionNo: number;
    processInstancePublicId: string;
    workflowTaskPublicId: string;
    businessTaskId: string;
    businessObjectType: string;
    businessObjectPublicId: string;
    recipientType: 'TODO' | 'DONE' | 'CC';
    allowedActions: Array<Pick<PortalWorkItemAction,
      'actionCode' | 'label' | 'requiresComment' | 'operationType'> & {
        formValidationPolicy: import('@jukun/platform-sdk/workflow')
          .WorkflowFormValidationPolicy;
      }>;
    baseFormPublicId: string;
  };
};

export type PortalInitiatedInstanceDetail = Omit<PortalWorkItemDetail,
  'pageRoute' | 'flowPageContext'> & {
  pageRoute: string | null;
  flowPageContext: null;
  processInitiationViewContext: import('@jukun/platform-sdk/workflow').ProcessInitiationViewContext;
  commentCount: number;
};

export type PortalTaskDetail = PortalWorkItemDetail | PortalInitiatedInstanceDetail;

export type PortalMyTaskCounts = Record<PortalTaskGroupKey, number> & {
  urgent: number;
};

export type PortalInitiatedInstanceListItem = {
  initiatedCardPublicId: string;
  processInstancePublicId: string;
  processDefinitionName: string;
  sourceSystem: string;
  title: string;
  summary: string;
  summaryItems: PortalWorkItemSummaryItem[];
  businessObjectType: string;
  businessObjectPublicId: string;
  sourceVersion: string;
  status: string;
  sourceState: string;
  initiator: string;
  initiatorAvatarUrl?: string;
  receivedAt: string;
  updatedAt: string;
};

export type PortalTransferTarget = {
  userId: string;
  name: string;
  avatarUrl?: string;
  organizationName?: string;
  positionName?: string;
};

export function formatShanghai(value?: string) {
  if (!value) return '-';
  return new Intl.DateTimeFormat('zh-CN', {
    timeZone: 'Asia/Shanghai',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).format(new Date(value)).replaceAll('/', '-');
}
